import { Box, Button, ButtonGroup } from '@material-ui/core';
import QRCodeStyling from 'qr-code-styling';
import React, { useRef, useState, useEffect } from 'react';
import { Record, useTranslate } from 'react-admin';
import CustomDialog from '../notes/subcomponents/CustomDialog';

interface Props {
    open: boolean;
    onClose: () => void;
    mc: Record;
}

const optionsMIME = [
    { label: 'jpeg', MIME: 'image/jpeg', format: 'jpeg' },
    { label: 'svg', MIME: 'image/svg', format: 'svg' },
    { label: 'png', MIME: 'image/png', format: 'png' },
];

const optionsSize = [
    { label: '200px', size: 200 },
    { label: '300px', size: 300 },
    { label: '400px', size: 400 },
];

const ShowQrDialog: React.FC<Props> = ({ open, onClose, mc }) => {
    const translate = useTranslate();
    const qrCodeContainer = useRef<HTMLDivElement>(null);
    const qrCodeInstance = useRef<QRCodeStyling | null>(null);
    const [selectedMIME, setSelectedMIME] = useState(optionsMIME[0]);
    const [selectedSize, setSelectedSize] = useState<any>(optionsSize[0]);

    // Efecto para inicializar el QR cuando se abre el diálogo
    useEffect(() => {
        if (!open) return;
        
        // Pequeño delay para asegurar que el contenedor esté disponible
        const timer = setTimeout(() => {
            const qrCode = new QRCodeStyling({
                width: selectedSize.size,
                height: selectedSize.size,
                data: mc?.yourls?.url || mc?.yourls?.mcUrl || '',
                margin: 1, // Borde blanco de 1px
                qrOptions: {
                    errorCorrectionLevel: 'M'
                },
                dotsOptions: {
                    color: '#000000',
                    type: 'square'
                },
                backgroundOptions: {
                    color: '#ffffff'
                }
            });
            
            qrCodeInstance.current = qrCode;
            
            if (qrCodeContainer.current) {
                qrCodeContainer.current.innerHTML = '';
                qrCode.append(qrCodeContainer.current);
            }
        }, 100);
        
        return () => clearTimeout(timer);
    }, [open, selectedSize.size, mc]);

    // Efecto para regenerar el QR cuando cambie el tamaño
    useEffect(() => {
        if (!open || !qrCodeInstance.current || !qrCodeContainer.current) return;
        
        // Regenerar completamente el QR con el nuevo tamaño
        const qrCode = new QRCodeStyling({
            width: selectedSize.size,
            height: selectedSize.size,
            data: mc?.yourls?.url || mc?.yourls?.mcUrl || '',
            margin: 1, // Borde blanco de 1px
            qrOptions: {
                errorCorrectionLevel: 'M'
            },
            dotsOptions: {
                color: '#000000',
                type: 'square'
            },
            backgroundOptions: {
                color: '#ffffff'
            }
        });
        
        qrCodeInstance.current = qrCode;
        qrCodeContainer.current.innerHTML = '';
        qrCode.append(qrCodeContainer.current);
    }, [open, selectedSize.size, mc]);

    const downloadQrCode = () => {
        if (!qrCodeInstance.current) return;
        
        const { format } = selectedMIME;
        const extension = format === 'svg' ? 'svg' : format as 'png' | 'jpeg';
        
        qrCodeInstance.current.download({
            name: mc?.name || 'qr-code',
            extension: extension
        });
    };

    const buttons = (
        <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={onClose} variant='contained' color='primary'>
                {translate('ra.action.close')}
            </Button>
        </Box>
    );

    return (
        <CustomDialog
            open={open}
            onClose={onClose}
            title={translate('app.objects.qrCode')}
            icon={<img src="/images/qr-icon.svg" alt="QR Code" style={{ width: 24, height: 24 }} />}
            width='sm'
            buttons={buttons}
        >
            <Box
                style={{
                    display: 'grid',
                    gap: 16,
                    paddingTop: 8,
                }}
            >
                <Box
                    style={{
                        display: 'grid',
                        gap: 16,
                        justifyContent: 'center',
                        justifyItems: 'center',
                    }}
                >
                    <div
                        ref={qrCodeContainer}
                        style={{
                            width: selectedSize.size,
                            height: selectedSize.size,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    />

                    <ButtonGroup variant='contained' size='small' disableElevation>
                        {optionsMIME.map((option) => (
                            <Button
                                key={option.label}
                                onClick={() => setSelectedMIME(option)}
                                color={selectedMIME.label === option.label ? 'primary' : 'default'}
                                variant={selectedMIME.label === option.label ? 'contained' : 'outlined'}
                            >
                                {option.label}
                            </Button>
                        ))}
                    </ButtonGroup>

                    <ButtonGroup variant='contained' size='small' disableElevation>
                        {optionsSize.map((optionSize) => (
                            <Button
                                key={optionSize.label}
                                onClick={() => setSelectedSize(optionSize)}
                                color={selectedSize.label === optionSize.label ? 'primary' : 'default'}
                                variant={selectedSize.label === optionSize.label ? 'contained' : 'outlined'}
                            >
                                {optionSize.label}
                            </Button>
                        ))}
                    </ButtonGroup>

                    <Button 
                        variant='contained' 
                        color='primary' 
                        onClick={downloadQrCode}
                        startIcon={<img src="/images/qr-icon.svg" alt="QR Code" style={{ width: 20, height: 20 }} />}
                    >
                        {translate('app.download')}
                    </Button>
                </Box>
            </Box>
        </CustomDialog>
    );
};

export default ShowQrDialog;
